import { Component, ChangeDetectionStrategy, computed, inject, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, map, of } from 'rxjs';
import { OcrPrimeStore } from '../../store/ocr-prime.store';
import { AnchorTreeService } from '../../services/anchor-tree.service';
import { AnchorTreeNode } from '../../types/anchor-tree';
import { AnchorTreeCanvasComponent } from '../../components/anchor-tree-canvas/anchor-tree-canvas.component';
import { NodeInspectorComponent } from './components/node-inspector/node-inspector.component';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../components/confirm-dialog/confirm-dialog.component';
import { collectLeaves } from '../../utils/anchor-tree.utils';
import {
  addBranch,
  countTemplatesInSubtree,
  createNode,
  findNode,
  removeNode,
  replaceNode
} from '../../utils/anchor-tree-edit.utils';

/**
 * Dedicated Anchor Tree authoring page (Q16-A): hybrid canvas + inspector.
 * The canvas owns structure (select, add/delete nodes and branches; dagre
 * auto-layout owns positions), the docked inspector owns detailed editing of
 * the selected node. Template assignment happens on the Templates page —
 * never here.
 */
@Component({
  selector: 'app-anchor-tree-page',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, MatTooltipModule, AnchorTreeCanvasComponent, NodeInspectorComponent],
  styles: [`:host { display: block; height: 100%; }`],
  template: `
    <div class="h-full flex flex-col p-6 overflow-hidden">
      <header class="flex items-center justify-between mb-6 flex-none">
        <div>
          <h1 class="text-2xl font-bold tracking-tight text-zinc-900 dark:text-zinc-100">Anchor Tree</h1>
          <p class="text-zinc-500 dark:text-zinc-400 mt-1">
            Author the decision tree that routes documents to template leaves. Templates are assigned on the Templates page.
          </p>
        </div>
        <div class="flex items-center gap-3">
          <button
            data-testid="anchor-tree-discard-btn"
            type="button"
            [disabled]="!isDirty() || isSaving()"
            (click)="discard()"
            class="inline-flex items-center gap-2 rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-4 py-2.5 text-sm font-medium text-zinc-700 dark:text-zinc-200 shadow-sm hover:bg-zinc-50 dark:hover:bg-zinc-600 cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Discard changes
          </button>
          <button
            data-testid="anchor-tree-save-btn"
            type="button"
            [disabled]="!isDirty() || isSaving()"
            (click)="save()"
            class="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-blue-700 cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            @if (isSaving()) {
              <div class="animate-spin rounded-full h-4 w-4 border-2 border-white/40 border-t-white"></div>
            } @else {
              <mat-icon class="!text-base !w-4 !h-4 !leading-4">save</mat-icon>
            }
            {{ isSaving() ? 'Saving…' : 'Save tree' }}
          </button>
        </div>
      </header>

      @if (error()) {
        <div class="mb-4 flex-none rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-800 dark:text-red-300"
             data-testid="anchor-tree-error">
          {{ error() }}
        </div>
      }
      @if (leafSetChangeHint()) {
        <div class="mb-4 flex-none rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-800 dark:text-amber-300"
             data-testid="anchor-tree-stale-hint">
          Leaf anchor sets changed — templates on affected leaves may need re-extraction. Check the Templates page for
          "Anchor mismatch" badges and re-extract + confirm per template.
        </div>
      }

      <main class="flex-1 flex gap-4 min-h-0">
        @if (store.anchorTreeLoading()) {
          <div class="flex flex-1 items-center justify-center py-16" aria-busy="true">
            <div class="animate-spin rounded-full h-6 w-6 border-2 border-zinc-300 dark:border-zinc-500 border-t-blue-500"></div>
          </div>
        } @else if (!draftRoot()) {
          <div class="flex flex-1 flex-col items-center justify-center py-16 text-center" data-testid="anchor-tree-empty">
            <mat-icon class="!text-4xl !w-10 !h-10 text-zinc-300 dark:text-zinc-600 mb-3">account_tree</mat-icon>
            <h3 class="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1">No tree yet</h3>
            <p class="text-xs text-zinc-500 dark:text-zinc-400 max-w-[300px] mb-4">
              Start by adding a root node: a decision node routes on an anchor field; a leaf holds templates directly.
            </p>
            <div class="flex gap-2">
              <button
                data-testid="anchor-tree-add-root-decision"
                type="button"
                (click)="addRoot('decision')"
                class="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-3 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-200 shadow-sm hover:bg-zinc-50 dark:hover:bg-zinc-600 cursor-pointer transition-colors"
              >
                <mat-icon class="!text-base !w-4 !h-4 !leading-4">alt_route</mat-icon>
                Add decision root
              </button>
              <button
                data-testid="anchor-tree-add-root-leaf"
                type="button"
                (click)="addRoot('leaf')"
                class="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-3 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-200 shadow-sm hover:bg-zinc-50 dark:hover:bg-zinc-600 cursor-pointer transition-colors"
              >
                <mat-icon class="!text-base !w-4 !h-4 !leading-4">eco</mat-icon>
                Add leaf root
              </button>
            </div>
          </div>
        } @else {
          <section class="flex-1 min-w-0 overflow-hidden rounded-xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50/30 dark:bg-zinc-800/30">
            <app-anchor-tree-canvas
              [root]="draftRoot()"
              [selectedNodeId]="selectedNodeId()"
              [editable]="true"
              (nodeSelected)="onNodeSelected($event)"
              (deleteRequested)="onDeleteRequested($event)"
            />
          </section>
          @if (selectedNode(); as node) {
            <aside class="w-96 flex-none min-h-0" data-testid="anchor-tree-inspector-panel">
              <app-node-inspector
                [node]="node"
                (changed)="onNodeChanged($event)"
                (addBranchRequested)="onAddBranch({ decisionNodeId: node.id, kind: $event })"
                (branchRemoveRequested)="onDeleteRequested($event)"
                (closeRequested)="onNodeSelected(null)"
              />
            </aside>
          }
        }
      </main>
    </div>
  `
})
export class AnchorTreePageComponent {
  readonly store = inject(OcrPrimeStore);
  private readonly anchorTreeService = inject(AnchorTreeService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);

  /** Draft edited locally; only persisted on explicit Save. */
  readonly draftRoot = signal<AnchorTreeNode | null>(this.cloneRoot());
  readonly isSaving = signal(false);
  readonly error = signal<string | null>(null);
  readonly leafSetChangeHint = signal(false);

  readonly selectedNodeId = signal<string | null>(null);
  readonly selectedNode = computed(() => {
    const id = this.selectedNodeId();
    return id ? findNode(this.draftRoot(), id) : null;
  });

  readonly isDirty = computed(
    () => JSON.stringify(this.draftRoot()) !== JSON.stringify(this.store.anchorTreeRoot())
  );

  constructor() {
    // If the tree was not yet loaded when the page constructed, sync the draft
    // once loading completes (platformDataResolver normally preloads it).
    this.store.loadAnchorTree();
  }

  addRoot(kind: 'decision' | 'leaf'): void {
    const root = createNode(kind);
    this.draftRoot.set(root);
    this.selectedNodeId.set(root.id);
  }

  onNodeSelected(nodeId: string | null): void {
    this.selectedNodeId.set(nodeId);
  }

  onNodeChanged(node: AnchorTreeNode): void {
    this.draftRoot.set(replaceNode(this.draftRoot(), node));
  }

  onAddBranch({ decisionNodeId, kind }: { decisionNodeId: string; kind: 'decision' | 'leaf' }): void {
    const { root } = addBranch(this.draftRoot(), decisionNodeId, kind);
    this.draftRoot.set(root);
    this.selectedNodeId.set(decisionNodeId);
  }

  /** Generalized subtree delete (canvas delete buttons + inspector branch remove). */
  onDeleteRequested(nodeId: string): void {
    const subtree = findNode(this.draftRoot(), nodeId);
    if (!subtree) {
      return;
    }
    const templateCount = countTemplatesInSubtree(subtree);
    if (templateCount === 0) {
      this.deleteSubtree(nodeId);
      return;
    }
    const isRoot = this.draftRoot()?.id === nodeId;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '440px',
      panelClass: 'field-dialog-panel',
      data: {
        title: isRoot ? 'Delete the whole tree?' : 'Delete this subtree?',
        message: `${templateCount} template(s) are assigned in this subtree and will become unplaced.`,
        consequences: ['Unplaced templates are unreachable for matching until re-assigned.'],
        confirmLabel: 'Delete'
      } as ConfirmDialogData
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.deleteSubtree(nodeId);
      }
    });
  }

  discard(): void {
    this.draftRoot.set(this.cloneRoot());
    this.selectedNodeId.set(null);
    this.error.set(null);
    this.leafSetChangeHint.set(false);
  }

  save(): void {
    this.isSaving.set(true);
    this.error.set(null);
    const leafSetsChanged = this.haveLeafAnchorSetsChanged();
    this.anchorTreeService.saveAnchorTree(this.draftRoot()).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.leafSetChangeHint.set(leafSetsChanged);
        this.store.refreshAnchorTree();
        this.snackBar.open('Anchor tree saved', 'Close', { duration: 3000 });
      },
      error: (err) => {
        this.isSaving.set(false);
        const message = err?.error?.message || err?.message || 'Failed to save the anchor tree';
        this.error.set(message);
      }
    });
  }

  /** Unsaved-changes guard hook (see unsavedChangesGuard). */
  canDeactivate(): Observable<boolean> {
    if (!this.isDirty()) {
      return of(true);
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '440px',
      panelClass: 'field-dialog-panel',
      data: {
        title: 'Discard unsaved tree changes?',
        message: 'You have unsaved changes to the anchor tree.',
        confirmLabel: 'Discard',
        cancelLabel: 'Keep editing'
      } as ConfirmDialogData
    });
    return dialogRef.afterClosed().pipe(map(confirmed => !!confirmed));
  }

  private deleteSubtree(nodeId: string): void {
    this.draftRoot.set(removeNode(this.draftRoot(), nodeId));
    // Clear the selection when the selected node was inside the deleted subtree.
    const selectedId = this.selectedNodeId();
    if (selectedId && !findNode(this.draftRoot(), selectedId)) {
      this.selectedNodeId.set(null);
    }
  }

  /** Q17 hint: detect whether any existing leaf's anchor set changed in the draft. */
  private haveLeafAnchorSetsChanged(): boolean {
    const savedLeaves = new Map(collectLeaves(this.store.anchorTreeRoot()).map(l => [l.id, l]));
    for (const draftLeaf of collectLeaves(this.draftRoot())) {
      const saved = savedLeaves.get(draftLeaf.id);
      if (!saved) continue;
      const a = [...(saved.leafAnchorFieldIds ?? [])].sort().join(',');
      const b = [...(draftLeaf.leafAnchorFieldIds ?? [])].sort().join(',');
      if (a !== b && (saved.templateIds?.length ?? 0) > 0) {
        return true;
      }
    }
    return false;
  }

  private cloneRoot(): AnchorTreeNode | null {
    const root = this.store.anchorTreeRoot();
    return root ? JSON.parse(JSON.stringify(root)) : null;
  }
}
